import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from "@angular/core";
import { Visit_DetailProjection } from "@app/models/rest/visit";
import { VisitStatus } from "@app/models/enums/visit-status.enum";
import moment from "moment";
import { MatStepper } from "@angular/material/stepper";
import { VisitStore } from "@app/services/store/visit-store";
import { Router, RouterLink } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { BookingNavigationService } from "@app/services/navigation/booking-navigation.service";
import { CancelVisitDialogComponent } from "@app/view/jobs/cancel-visit-dialog/cancel-visit-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { Ticket_BasicProjection } from "@app/models/rest/ticket";
import { TicketActionDialogComponent } from "@app/view/planning/ticket-action-dialog/ticket-action-dialog.component";
import { BookingConfirmComponent } from "@app/view/booking/booking-confirm/booking-confirm.component";
import { BookingOutcomeComponent } from "@app/view/booking/booking-outcome/booking-outcome.component";
import { AvailabilityVisitService } from "@app/services/http/availability-visit.service";
import { VisitAuditViewerComponent } from "@app/view/shared/audits/visit-audit-viewer/visit-audit-viewer.component";
import { selectorAuth } from "@app/core";
import { select, Store } from "@ngrx/store";
import { MatCard } from "@angular/material/card";
import { MatToolbar } from "@angular/material/toolbar";
import { NgClass } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { CalendarDateDisplayComponent } from "../../shared/calendar-date-display/calendar-date-display.component";
import { MatList, MatListItem } from "@angular/material/list";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatLine } from "@angular/material/core";
import { MomentModule } from "ngx-moment";
import { TicketExpandComponent } from "../../shared/ticket-expand/ticket-expand.component";

@Component({
  selector: "app-visit-card",
  templateUrl: "./visit-card.component.html",
  styleUrls: ["./visit-card.component.css"],
  imports: [
    MatCard,
    MatToolbar,
    NgClass,
    MatButton,
    CalendarDateDisplayComponent,
    MatList,
    MatListItem,
    MatIcon,
    MatTooltip,
    MatLine,
    MomentModule,
    TicketExpandComponent
  ]
})
export class VisitCardComponent implements OnInit, OnChanges {
  availabilityService = inject(AvailabilityVisitService);
  visitStore = inject(VisitStore);
  router = inject(Router);
  dialog = inject(MatDialog);
  serviceErrorHandler = inject(ServiceErrorHandlerService);
  private bookingNavigationService = inject(BookingNavigationService);
  protected store = inject<Store<any>>(Store);

  @Input()
  public visitId: number;
  public allowAudit: boolean = false;
  public visit: Visit_DetailProjection;

  @ViewChild("timeStepper", { static: false }) timeStepper: MatStepper;

  public timingsList: TimingEvent[] = [];
  public hide = false;
  public draft = false;
  public inProgress = false;
  public isProject = false;
  public showBookAppointmentButton = false;
  public showRebookAppointmentButton = false;
  public cancelVisit = false;
  public referVisit = false;

  public earliestDespatchedTime: Date = null;
  public earliestEnrouteTime: Date = null;
  public earliestArrivedTime: Date = null;
  public earliestStartedTime: Date = null;
  public earliestCompletedTime: Date = null;

  @Output() public cancelled = new EventEmitter<boolean>();

  constructor() {
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowAudit = !!auth && auth.allowAudit;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log("ng on changes loading visit id", this.visitId);
    if (this.visitId) {
      this.visitStore
        .loadSingleProjection(Visit_DetailProjection, this.visitId, "detail")
        .then((value) => {
          this.visit = value;
          this.buildTimingsList();
        });
    }
  }

  getStatusText(visitStatus: string): string {
    switch (visitStatus) {
      case "COMPLETED":
        return "Completed";
      case "NEW":
        return "Not booked";
      case "REFERRED":
        return "Referred";
      case "CANCELLED":
        return "Cancelled";
      case "IN_PROGRESS":
        return "In progress";
      case "BOOKED":
        return "Booked";
      case "FLOATING":
        return "Floating Booking";
      default:
        return "";
    }
  }

  ngOnInit(): void {
    console.log("Init called", this.visitId);
    this.ngOnChanges(null);
  }

  public buildTimingsList() {
    console.log("building timings list");
    console.log(JSON.stringify(this.visit));
    this.showBookAppointmentButton = false;
    this.showRebookAppointmentButton = false;
    this.referVisit = false;
    this.cancelVisit = false;
    this.inProgress = false;
    this.timingsList = [];
    if (this.visit.job.externalProjectReference) {
      this.isProject = true;
    } else {
      if (this.visit.visitStatus === VisitStatus.AUTO) {
        this.showBookAppointmentButton = true;
        this.cancelVisit = true;
        this.referVisit = true;
      }
      if (this.visit.visitStatus === VisitStatus.REFERRED) {
        this.showBookAppointmentButton = true;
        this.cancelVisit = true;
      }
      if (this.visit.visitStatus === VisitStatus.NEW) {
        this.showBookAppointmentButton = true;
        this.cancelVisit = true;
      }
      if (
        this.visit.visitStatus === VisitStatus.BOOKED ||
        this.visit.visitStatus === VisitStatus.FLOATING
      ) {
        this.draft = true;
        this.showRebookAppointmentButton = true;
        this.referVisit = true;
        this.cancelVisit = true;
      }
      if (this.visit.visitStatus === VisitStatus.IN_PROGRESS) {
        this.inProgress = true;
      }
    }

    if (
      this.visit.visitStatus === VisitStatus.BOOKED ||
      this.visit.visitStatus === VisitStatus.FLOATING ||
      this.visit.visitStatus === VisitStatus.IN_PROGRESS ||
      this.visit.visitStatus === VisitStatus.CANCELLED ||
      this.visit.visitStatus === VisitStatus.COMPLETED
    ) {
      this.inProgress = true;
      let upperText: string;

      if (this.visit.visitStatus === VisitStatus.FLOATING) {
        // Special handling for floating bookings - show date range
        upperText = `Floating: ${moment(this.visit.appointmentStart).format(
          "DD/MM/YYYY"
        )} - ${moment(this.visit.appointmentEnd).format("DD/MM/YYYY")}`;
      } else if (moment(this.visit.appointmentStart).isSame(moment(), "day")) {
        upperText = ` ${moment(this.visit.appointmentStart).format(
          "HH:mm"
        )} - ${moment(this.visit.appointmentEnd).format("HH:mm")} Today`;
      } else {
        upperText = `${moment(this.visit.appointmentStart).format(
          "DD/MM/YYYY HH:mm"
        )} - ${moment(this.visit.appointmentEnd).format("HH:mm")}`;
      }

      this.timingsList.push({
        icon: "fa-clock",
        lowerText: "Appointment",
        timePoint: this.visit.appointmentStart,
        upperText: upperText
      });
    }

    let properties = [
      { store: "earliestDespatchedTime", object: "despatchedTime" },
      { store: "earliestEnrouteTime", object: "enrouteTime" },
      { store: "earliestArrivedTime", object: "arrivedTime" },
      { store: "earliestStartedTime", object: "startedTime" },
      { store: "earliestCompletedTime", object: "completedTime" }
    ];

    for (let ticket of this.visit.ticketList) {
      for (let property of properties) {
        if (ticket[property.object]) {
          // console.log('saw', ticket[property.object]);
          if (this[property.store]) {
            if (
              moment(ticket[property.object]).isBefore(this[property.store])
            ) {
              this[property.store] = ticket[property.object];
            }
          } else {
            this[property.store] = ticket[property.object];
          }
        }
      }
    }
  }

  cancelAppointmentAction() {
    this.dialog.open(CancelVisitDialogComponent, {
      data: { visit: this.visit }
    });
  }

  openReferralForm() {
    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: this.visit,
        referralMode: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.availabilityService
        .bookVisit({
          visitId: this.visit.id,
          startTime: null,
          endTime: null,
          force: false,
          bookingReasonId: result.bookingReasonId,
          notes: result.notes,
          displayedSlotRanking: null,
          refer: true,
          cancel: false,
          tickets: null
        })
        .then((response) => {
          // this.ngxLoader.stopLoader("bookingLoader", "confirm");
          let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: response.success,
              message: response.message,
              mode: "REFER"
            }
          });
          bookingOutcomeRef.afterClosed().subscribe((outcome) => {
            console.log(response.success);
            if (response.success) {
              this.ngOnInit();
            } else {
              this.ngOnInit();
            }
          });
        })
        .catch((reason) => {
          let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: false,
              message: "Error in booking service call"
            }
          });
          bookingOutcomeRef.afterClosed().subscribe((outcome) => {});
        });
    });
  }

  openTicketDialog(ticket: Ticket_BasicProjection) {
    this.dialog.open(TicketActionDialogComponent, {
      hasBackdrop: true,
      width: "500px",
      panelClass: "toolbar-popup-dialog",
      data: {
        ticketId: ticket.id
      }
    });
  }

  openCancelForm() {
    const dialogRef = this.dialog.open(BookingConfirmComponent, {
      width: "450px",
      data: {
        mode: "EDIT",
        visit: this.visit,
        cancellationMode: true
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.availabilityService
        .bookVisit({
          visitId: this.visit.id,
          startTime: null,
          endTime: null,
          force: false,
          bookingReasonId: result.bookingReasonId,
          notes: result.notes,
          displayedSlotRanking: null,
          refer: false,
          cancel: true,
          tickets: null
        })
        .then((response) => {
          // this.ngxLoader.stopLoader("bookingLoader", "confirm");
          let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: response.success,
              message: response.message,
              mode: "CANCEL"
            }
          });
          bookingOutcomeRef.afterClosed().subscribe((outcome) => {
            console.log(response.success);
            if (response.success) {
              // if (this.exitOnCompletion) {
              //   console.log("Exit on completion mode");
              //   if (this.windowClose) {
              //     console.log("Window is closing");
              //     window.close();
              //   }
              //   this.booked.emit({ booked: true });
              //   this.closed = true;
              //   this.ngxLoader.stopLoader("bookingLoader", "confirm");
              // } else {
              //   this.location.back();
              // }

              this.ngOnInit();
            } else {
              this.ngOnInit();
            }
          });
        })
        .catch((reason) => {
          let bookingOutcomeRef = this.dialog.open(BookingOutcomeComponent, {
            data: {
              success: false,
              message: "Error in booking service call"
            }
          });
          bookingOutcomeRef.afterClosed().subscribe((outcome) => {
            // this.booked.emit({ booked: false });
            // this.ngxLoader.stopLoader("bookingLoader", "confirm");
          });
        });
    });
  }

  openVisitAudit() {
    this.dialog.open(VisitAuditViewerComponent, {
      width: "calc(100vw - 100px)",
      data: {
        visitId: this.visitId
      }
    });
  }

  // Helper method to calculate days between two dates for floating bookings
  getDaysRange(): number {
    if (
      !this.visit ||
      !this.visit.appointmentStart ||
      !this.visit.appointmentEnd
    ) {
      return 0;
    }

    const startDate = moment(this.visit.appointmentStart);
    const endDate = moment(this.visit.appointmentEnd);

    // Add 1 to include both start and end days
    return endDate.diff(startDate, "days") + 1;
  }

  bookAppointment(): void {
    // Store the current route so booking components can return here
    const originalRoute = this.determineOriginRoute();
    this.bookingNavigationService.setOriginalRoute(originalRoute);
    this.router.navigate(["/booking/visit/", this.visit.id]);
  }

  rebookAppointment(): void {
    // Store the current route so booking components can return here
    const originalRoute = this.determineOriginRoute();
    this.bookingNavigationService.setOriginalRoute(originalRoute);
    this.router.navigate(["/booking/visit/", this.visit.id]);
  }

  private determineOriginRoute(): string {
    const currentUrl = this.router.url;
    console.log("VisitCard: Current URL for origin determination:", currentUrl);

    // If we're in a job detail page context
    if (currentUrl.includes("/jobs/")) {
      return currentUrl;
    }

    // If we're in a data view (Views menu) context
    if (currentUrl.includes("/data-view/visits")) {
      return "/data-view/visits";
    }

    // If we're in the booking/appointments context
    if (currentUrl.includes("/booking")) {
      return "/booking";
    }

    // Default fallback - use current URL
    console.log("VisitCard: Using fallback origin route:", currentUrl);
    return currentUrl;
  }
}

class TimingEvent {
  public icon: string;
  public upperText: string;
  public lowerText: string;
  public timePoint: Date;
}
